"use client";

import React, { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { X } from "lucide-react";

type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl";

const sizeStyles: Record<ModalSize, { width: string }> = {
  sm: { width: "24rem" }, // 384px
  md: { width: "28rem" }, // 448px
  lg: { width: "32rem" }, // 512px
  xl: { width: "60rem" }, // 960px
  "2xl": { width: "82rem" }, // 1312px
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: ModalSize;
  fullscreen?: boolean;
  closeButton?: boolean;
  zIndexBase?: number;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  fullscreen = false,
  closeButton = false,
  zIndexBase = 1000,
}: ModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/80"
        style={{ zIndex: zIndexBase }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4 overflow-hidden "
        style={{ zIndex: zIndexBase + 10 }}
      >
        <div
          className={cn(
            "bg-white rounded-lg shadow-lg",
            fullscreen
              ? "w-full h-full max-h-[90vh] overflow-hidden flex flex-col custom-scrollbar"
              : "overflow-auto max-h-full custom-scrollbar",
          )}
          style={
            fullscreen
              ? {
                  width: "calc(100vw - 2rem)",
                  maxWidth: "calc(100vw - 2rem)",
                }
              : {
                  // El ancho fijo por tamaño nunca debe rebasar la pantalla:
                  // en móvil se recorta a (viewport - padding del overlay).
                  width: `min(${sizeStyles[size].width}, calc(100vw - 2rem))`,
                  maxHeight: "calc(100dvh - 2rem)",
                }
          }
        >
          {/* Header */}
          <div
            className="flex items-center justify-between p-2 align-items-center border-b sticky top-0 z-10 bg-white shrink-0"
          >
            <p className="text-md font-bold p-2">{title}</p>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none cursor-pointer hover:text-red-500"
            >
              <X />
            </button>
          </div>

          <div
            className={cn(
              "p-2",
              fullscreen ? "flex-1 overflow-y-auto custom-scrollbar" : "overflow-auto",
            )}
          >
            {children}
          </div>

          {/* Footer */}
          {closeButton && (
            <div
              className={cn(
                "flex gap-2 p-2 border-t justify-end",
                fullscreen ? "sticky bottom-0 " : "",
              )}
            >
              <Button variant="outline" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
